import React, { useMemo } from "react";
import * as THREE from "three";
import { Edges } from "@react-three/drei";
import { computeOrientation } from "../transformations";
import { ElementCoordinateSystem } from "../common_types";
import { useStoreSelector } from "../../../../../../../../hooks/StoreHooks";
import { InstallationType } from "../../forms/Pergola_type";
import { a, useSpring } from "@react-spring/three";
import { LatoPergola } from "../Pergola";
import { NoTypeSide, TendeType, VetrateType } from "../../../../../../../models/pergola_configuration";

interface GuidaUpProps {
  lunghezza: number;
  material: THREE.Texture;
  cs: ElementCoordinateSystem;
}

interface GuidaBelowProps {
  lunghezza: number;
  material: THREE.Texture;
  cs: ElementCoordinateSystem;
}

interface VetroProps {
  lunghezza: number;
  material: THREE.Texture;
  cs: ElementCoordinateSystem;
  altezzaPilastro: number;
  slideOffset: number;
  muro: number;
  axis: 'x' | 'y';
}

interface CompleteVetrataAndromedaProps {
  lunghezzaSoletta: number;
  larghezzaSoletta: number;
  altezzaPilastro: number;
  sezionePilastro: { larghezza: number; altezza: number };
  sezioneTrave: { larghezza: number; altezza: number };
  texture: THREE.Texture;
  lato: LatoPergola;
  isOpen?: boolean;
  needExtra: boolean;
  muro: number;
  segmentiLiberi: { start: number; end: number }[];
}

type Sezione = { larghezza: number; altezza: number };

const sezioneGuidaUp: Sezione = { larghezza: 0.04, altezza: 0.04 };
const sezioneGuidaBelow: Sezione = { larghezza: 0.043, altezza: 0.034 };
const sezioneVetro: Sezione = { larghezza: 0.01, altezza: 0.01 };

/**
 * Guida superiore
 */
const GuidaUp: React.FC<GuidaUpProps> = ({ lunghezza, material, cs }) => {
  const rotation = useMemo<[number, number, number]>(
    () => computeOrientation(cs.direction) as [number, number, number],
    [cs.direction]
  );
  return (
    <mesh position={[cs.org.x, cs.org.y, cs.org.z - sezioneGuidaUp.altezza / 2]} rotation={rotation}>
      <boxGeometry args={[sezioneGuidaUp.larghezza, sezioneGuidaUp.altezza, lunghezza]} />
      <meshStandardMaterial map={material} />
      <Edges color="#363636" />
    </mesh>
  );
};

/**
 * Guida inferiore
 */
const GuidaBelow: React.FC<GuidaBelowProps> = ({ lunghezza, material, cs }) => {
  const rotation = useMemo<[number, number, number]>(
    () => computeOrientation(cs.direction) as [number, number, number],
    [cs.direction]
  );
  return (
    <mesh position={[cs.org.x, cs.org.y, cs.org.z + sezioneGuidaBelow.altezza / 2]} rotation={rotation}>
      <boxGeometry args={[sezioneGuidaBelow.larghezza, sezioneGuidaBelow.altezza, lunghezza]} />
      <meshStandardMaterial map={material} />
      <Edges color="#363636" />
    </mesh>
  );
};

/**
 * Pannello vetro con animazione
 */
const Vetro: React.FC<VetroProps> = ({
  lunghezza,
  material,
  muro,
  cs,
  altezzaPilastro,
  slideOffset,
  axis
}) => {
  const rotation = useMemo<[number, number, number]>(
    () => computeOrientation(cs.direction) as [number, number, number],
    [cs.direction]
  );

  const { offset } = useSpring({
    offset: slideOffset,
    config: { mass: 1, tension: 220, friction: 26 }
  });

  const position = offset.to((o) => [
    cs.org.x + (axis === "x" ? o : 0),
    cs.org.y + (axis === "y" ? o : 0),
    cs.org.z + altezzaPilastro/2 + muro
  ]) as unknown as [number, number, number];

  return (
    <a.mesh position={position} rotation={rotation}>
      <boxGeometry args={[sezioneVetro.larghezza, altezzaPilastro, lunghezza]} />
      <meshPhysicalMaterial
        transparent
        opacity={0.25}
        transmission={0.95}
        depthWrite={false}
        polygonOffset
        polygonOffsetFactor={-1}
        polygonOffsetUnits={-1}
      />
      <Edges color="#363636" />
    </a.mesh>
  );
};

/**
 * Calcola parametri per un singolo segmento
 */
const getSegmentoVetrataParams = (
  direzione: string,
  start: number,
  end: number,
  installazione: InstallationType | undefined,
  altezzaPilastro: number,
  larghezzaSoletta: number,
  lunghezzaSoletta: number,
  sezionePilastro: Sezione,
  sezioneTrave: Sezione,
  needExtra: boolean,
  muro: number,
  isOpen: boolean,
  lato: LatoPergola,
  verso?: string
) => {
  const lunghezzaSegmento = end - start;
  const centro = (start + end) / 2;
  const offsetExtra = needExtra ? sezioneTrave.altezza - 0.02 : 0;

  function isVetrateType(tipologia: VetrateType | TendeType | NoTypeSide): tipologia is VetrateType {
    return (tipologia as VetrateType).vie !== undefined;
  }

  let maxVetrate = 2;
  let isCentrale = false;

  if (isVetrateType(lato.tipoLato.tipologia)) {
    maxVetrate = lato.tipoLato.tipologia.vie || 2;
    if (lato.tipoLato.tipologia.apertura === 'centrale') {
      isCentrale = true;
      maxVetrate *= 2; // il configuratore già moltiplica per 2, lo manteniamo coerente
    }
  }

  const lunghezzaVetro = (
    lunghezzaSegmento +
    (
      lato.segmento <= 1 &&
      lato.direzione !== 'Sinistra' &&
      lato.direzione !== 'Destra'
        ? 0.08
        : (lato.direzione === 'Sinistra' || lato.direzione === 'Destra') && lato.segmento > 1
          ? 0
          : (lato.direzione === 'Sinistra' || lato.direzione === 'Destra') && lato.segmento <= 1
          ? 0
          : sezionePilastro.larghezza + 0.02
    ) + (
      maxVetrate >= 5 && maxVetrate < 7 && lato.segmento <= 1
      ? 0.05
      : maxVetrate >= 7 && lato.segmento <= 1
      ? 0.32
      : 0
    )
  ) / maxVetrate;

  // Posizione base
  let direction: [number, number, number] = [0, 0, 0];
  let xBase = 0, yBase = 0;

  switch (direzione) {
    case "Destra":
      direction = [0, 1, 0];
      xBase = larghezzaSoletta - sezioneTrave.larghezza / 2;
      yBase = centro;
      break;
    case "Sinistra":
      direction = [0, -1, 0];
      xBase = sezioneTrave.larghezza / 2;
      yBase = centro;
      break;
    case "Fronte":
      direction = [1, 0, 0];
      xBase = centro;
      yBase = sezioneTrave.larghezza / 2 + 0.02;
      break;
    case "Retro":
      direction = [-1, 0, 0];
      xBase = centro;
      yBase = lunghezzaSoletta - sezioneTrave.larghezza / 2 - 0.02;
      break;
  }

  const axis = Math.abs(direction[0]) === 1 ? ("x" as const) : ("y" as const);
  const dirAlongAxis = axis === "x" ? direction[0] : direction[1];

  const getOrg = (i: number) => {
    const startF = start - (lato.segmento <= 1 ? 0 : sezionePilastro.larghezza + 0.02);
    const offset = i * (lunghezzaVetro - (lato.segmento <= 1 ? 0.04 : 0));
    if (direzione === "Destra" || direzione === "Sinistra") {
      return { x: xBase, y: start + lunghezzaVetro / 2 + offset, z: 0 };
    } else {
      return { x: startF + lunghezzaVetro / 2 + offset, y: yBase, z: 0 };
    }
  };

  const orgPositions = Array.from({ length: maxVetrate }, (_, i) => getOrg(i)[axis]);

  // Calcolo dell'offset per apertura centrale o laterale
  const getTargetOffset = (i: number): number => {
    if (!isOpen) return 0;

    const originalPos = getOrg(i)[axis];

    if (isCentrale) {
      // Dividiamo i pannelli in due gruppi: sinistra e destra
      const half = Math.floor(maxVetrate / 2);
      const isLeftGroup = i < half;

      // Determiniamo il pannello più esterno di ciascun gruppo
      const leftmost = Math.min(...orgPositions.slice(0, half));
      const rightmost = Math.max(...orgPositions.slice(half));

      // I pannelli del gruppo sinistro vanno verso il pannello più a sinistra
      // I pannelli del gruppo destro verso il pannello più a destra
      const target = isLeftGroup ? leftmost : rightmost;

      // Piccolo offset per evitare sovrapposizioni perfette
      const stackOrder = isLeftGroup ? i : (i - half);
      const smallOffset = stackOrder * sezioneVetro.larghezza * 0.1;

      return target - originalPos + (isLeftGroup ? -smallOffset : smallOffset);
    } else {
      // Comportamento originale: tutti i pannelli si impacchettano verso un lato
      // Se verso è definito lo usa; altrimenti ricade su dirAlongAxis
      const goRight = verso !== undefined ? verso === "Destra" : dirAlongAxis > 0;

      const firstPanelPos = goRight
        ? Math.max(...orgPositions)
        : Math.min(...orgPositions);

      const stackOrder = goRight ? (maxVetrate - 1 - i) : i;
      const smallOffset = stackOrder * sezioneVetro.larghezza * 0.1;

      return firstPanelPos - originalPos + smallOffset;
    }
  };

  return {
    lunghezzaSegmento,
    maxVetrate,
    lunghezzaVetro,
    direction,
    xBase,
    yBase,
    axis,
    dirAlongAxis,
    getOrg,
    getTargetOffset,
    guidaUpZ: altezzaPilastro - offsetExtra,
    guidaBelowZ: muro,
  };
};

/**
 * COMPONENTE PRINCIPALE
 */
export const CompleteVetrataMaia: React.FC<CompleteVetrataAndromedaProps> = ({
  lunghezzaSoletta,
  larghezzaSoletta,
  altezzaPilastro,
  sezionePilastro,
  sezioneTrave,
  texture,
  lato,
  isOpen = false,
  needExtra,
  muro,
  segmentiLiberi,
}) => {
  const pergolaConfiguration = useStoreSelector((store) => store.pergolaConfigurationBuilder);
  const installazione = pergolaConfiguration.configuration?.dimensioni.installazione;
  
  return (
    <>
      {segmentiLiberi.map((seg, segIdx) => {
        const verso = (lato.tipoLato.tipologia as VetrateType).verso;
        const p = getSegmentoVetrataParams(
          lato.direzione,
          seg.start,
          seg.end,
          installazione,
          altezzaPilastro,
          larghezzaSoletta,
          lunghezzaSoletta,
          sezionePilastro,
          sezioneTrave,
          needExtra,
          muro,
          isOpen,
          lato,
          verso
        );

        return (
          <group key={`${lato}-vetrata-segmento-${segIdx}`}>
            <GuidaUp
              lunghezza={p.lunghezzaSegmento - (
                lato.segmento <= 1 &&
                lato.direzione !== 'Sinistra' &&
                lato.direzione !== 'Destra'
                  ? 0.04
                  : (lato.direzione === 'Sinistra' || lato.direzione === 'Destra') && lato.segmento >= 1
                    ? 0.01
                    : -sezionePilastro.larghezza + 0.02
              )}
              material={texture}
              cs={{ org: { x: p.xBase, y: p.yBase, z: p.guidaUpZ }, direction: p.direction }}
            />
            <GuidaBelow
              lunghezza={p.lunghezzaSegmento - (
                lato.segmento <= 1 &&
                lato.direzione !== 'Sinistra' &&
                lato.direzione !== 'Destra'
                  ? 0.04
                  : (lato.direzione === 'Sinistra' || lato.direzione === 'Destra') && lato.segmento >= 1
                    ? 0.01
                    : -sezionePilastro.larghezza + 0.02
              )}
              material={texture}
              cs={{ org: { x: p.xBase, y: p.yBase, z: p.guidaBelowZ }, direction: p.direction }}
            />
            
            {/* Vetri */}
            {Array.from({ length: p.maxVetrate }, (_, i) => {
              const org = p.getOrg(i);
              const offset = p.getTargetOffset(i);
              return (
                <Vetro
                  key={i}
                  lunghezza={p.lunghezzaVetro - (lato.segmento <= 1 ? 0.04 : 0.01)}
                  material={texture}
                  muro={muro}
                  cs={{ org, direction: p.direction }}
                  altezzaPilastro={altezzaPilastro - muro}
                  slideOffset={offset}
                  axis={p.axis}
                />
              );
            })}
          </group>
        );
      })}
    </>
  );
};